import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, FileText, AlertTriangle, History, LogOut,
  Users, Clock, CheckCircle, XCircle, Loader2, TrendingUp, Building,
  Monitor, MapPin, Store, Gift, BarChart3, Briefcase, DollarSign,
  Shield, Activity, Camera, ClipboardList, Wallet, Package, RefreshCw,
  ParkingCircle, ArrowUpRight, ArrowDownRight, Timer, Banknote, Eye,
  Video, CreditCard, Settings, Megaphone, ListChecks, ChevronRight, LogIn
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import axios from "axios";

const API = '/api';

// Format currency in pence to pounds
const formatCurrency = (pence) => {
  if (pence === undefined || pence === null) return null;
  return `£${(pence / 100).toFixed(2)}`;
};

// Format duration in minutes
const formatDuration = (minutes) => {
  if (minutes === undefined || minutes === null) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [operationalData, setOperationalData] = useState(null);
  const [operationalApiAvailable, setOperationalApiAvailable] = useState(true);
  const [sessions, setSessions] = useState({ active: 0, completed_today: 0, total: [] });
  const [sites, setSites] = useState([]);
  // Persist selected site to localStorage for cross-page sharing
  const [selectedSite, setSelectedSiteState] = useState(() => {
    return localStorage.getItem('adminSelectedSite') || "all";
  });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Wrapper to persist site selection
  const setSelectedSite = (siteId) => {
    setSelectedSiteState(siteId);
    localStorage.setItem('adminSelectedSite', siteId);
  };

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      fetchAllData();
      return;
    }
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) fetchAllData();
  }, [selectedSite, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchAllData();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const siteParam = selectedSite !== "all" ? `?site_id=${selectedSite}` : "";
      
      // Fetch all data in parallel
      const [sitesRes, operationalRes, sessionsRes] = await Promise.allSettled([
        axios.get(`${API}/sites`, { withCredentials: true }),
        axios.get(`${API}/dashboard/operational${siteParam}`, { withCredentials: true }),
        axios.get(`${API}/parking/sessions${siteParam}`, { withCredentials: true })
      ]);

      if (sitesRes.status === 'fulfilled') setSites(sitesRes.value.data || []);
      
      // Track operational API availability
      if (operationalRes.status === 'fulfilled') {
        setOperationalData(operationalRes.value.data);
        setOperationalApiAvailable(true);
      } else {
        setOperationalData(null);
        setOperationalApiAvailable(false);
      }
      
      if (sessionsRes.status === 'fulfilled') {
        const allSessions = sessionsRes.value.data?.sessions || [];
        const activeSessions = allSessions.filter(s => s.status === 'active');
        const today = new Date().toISOString().split('T')[0];
        const completedToday = allSessions.filter(s => 
          s.status === 'completed' && s.exit_time?.startsWith(today)
        );
        
        // Sort sessions by entry_time descending (newest first) on frontend
        const sortedSessions = [...allSessions].sort((a, b) => {
          const timeA = new Date(a.entry_time).getTime();
          const timeB = new Date(b.entry_time).getTime();
          return timeB - timeA; // Descending: newest first
        });
        
        setSessions({
          active: activeSessions.length,
          completed_today: completedToday.length,
          total: sortedSessions
        });
      }
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('session_token');
    navigate("/admin/login");
  };

  const getSelectedSiteName = () => {
    if (selectedSite === "all") return "All Sites";
    const site = sites.find(s => s.site_id === selectedSite);
    return site ? site.name : selectedSite;
  };

  if (loading) {
    return (
      <div className="admin-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  // Derive operational metrics with null safety
  const dataAvailable = operationalApiAvailable && operationalData !== null;
  const vehiclesEntered = dataAvailable ? operationalData.vehicles_entered_today : null;
  const vehiclesExited = dataAvailable ? operationalData.vehicles_exited_today : null;
  const revenueToday = dataAvailable ? operationalData.revenue_today_pence : null;
  const ocrExceptions = dataAvailable ? operationalData.ocr_exceptions : null;
  const revenueWeek = dataAvailable ? operationalData.revenue_week_pence : null;
  const avgSpendToday = dataAvailable ? operationalData.avg_spend_today_pence : null;
  // Entry/Exit breakdowns
  const whitelistEntries = dataAvailable ? operationalData.whitelist_entries_today : null;
  const payableEntries = dataAvailable ? operationalData.payable_entries_today : null;
  const whitelistExits = dataAvailable ? operationalData.whitelist_exits_today : null;
  const paidExits = dataAvailable ? operationalData.paid_exits_today : null;
  const freeExits = dataAvailable ? operationalData.free_exits_today : null;
  // Live Sessions
  const liveSessionsTotal = dataAvailable ? operationalData.live_sessions_total : null;
  const livePayableSessions = dataAvailable ? operationalData.live_payable_sessions : null;
  const liveWhitelistedSessions = dataAvailable ? operationalData.live_whitelisted_sessions : null;
  // Reconciliation
  const entryReconciliationOk = dataAvailable ? operationalData.entry_reconciliation_ok : null;

  return (
    <div className="admin-container min-h-screen" data-testid="admin-dashboard">
      {/* Sidebar - keeping existing navigation */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        {/* Site Selector */}
        <div className="mb-6">
          <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Site Filter</label>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white" data-testid="site-selector">
              <MapPin className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="space-y-1">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-slate-800 text-white text-sm" data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" />
            <span>Dashboard</span>
          </Link>
          
          {/* Barrier Operations */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Operations</p>
            <Link to="/admin/freeflow" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Shield className="w-4 h-4" />
              <span>Freeflow Whitelist</span>
            </Link>
            <Link to="/admin/exit-auths" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Clock className="w-4 h-4" />
              <span>Exit Authorisations</span>
            </Link>
            <Link to="/admin/barrier" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Activity className="w-4 h-4" />
              <span>Barrier Control</span>
            </Link>
            <Link to="/admin/cameras" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Camera className="w-4 h-4" />
              <span>Cameras</span>
            </Link>
            <Link to="/admin/cctv" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm" data-testid="nav-cctv">
              <Video className="w-4 h-4" />
              <span>CCTV</span>
            </Link>
            <Link to="/admin/daily-movement" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <ClipboardList className="w-4 h-4" />
              <span>Daily VRM Movement</span>
            </Link>
            <Link to="/admin/vehicle-history" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <History className="w-4 h-4" />
              <span>Vehicle History</span>
            </Link>
          </div>
          
          {/* Commercial */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <DollarSign className="w-4 h-4" />
              <span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Package className="w-4 h-4" />
              <span>Products & Tariffs</span>
            </Link>
            <Link to="/admin/commercial/sites" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Building className="w-4 h-4" />
              <span>Sites</span>
            </Link>
          </div>

          {/* Admin */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Admin</p>
            <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <FileText className="w-4 h-4" />
              <span>Registrations</span>
            </Link>
            <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Building className="w-4 h-4" />
              <span>Sites & Kiosks</span>
            </Link>
            <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>OCR Exceptions</span>
              {ocrExceptions > 0 && (
                <span className="ml-auto bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {ocrExceptions}
                </span>
              )}
            </Link>
          </div>

          {/* Engagement */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Engagement</p>
            <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Store className="w-4 h-4" />
              <span>Retailers</span>
            </Link>
            <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Gift className="w-4 h-4" />
              <span>Rewards</span>
            </Link>
            <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </Link>
          </div>

          {/* Signage */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Monitor className="w-4 h-4" />
              <span>Screens</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <ListChecks className="w-4 h-4" />
              <span>Playlists</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <FileText className="w-4 h-4" />
              <span>Content</span>
            </Link>
          </div>

          {/* Revenue */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Megaphone className="w-4 h-4" />
              <span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>Campaigns</span>
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <CreditCard className="w-4 h-4" />
              <span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <BarChart3 className="w-4 h-4" />
              <span>Revenue</span>
            </Link>
          </div>

          {/* System */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">System</p>
            <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full" data-testid="logout-button">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <MapPin className="w-4 h-4" />
              <span>{getSelectedSiteName()}</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Executive Dashboard
            </h1>
            <p className="text-slate-400 text-sm">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <button 
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* PARKING ACTIVITY TODAY - Unified Flow Panel */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="parking-activity-panel">
          <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">Parking Activity Today</h2>
          
          {/* Three Column Grid: ENTERED → EXITED → LIVE NOW */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0">
            
            {/* ENTERED TODAY */}
            <div className="flex flex-col md:border-r md:border-slate-800 md:pr-4" data-testid="entered-today-section">
              <div className="flex items-center gap-2 mb-3">
                <LogIn className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-bold text-slate-200 uppercase tracking-wide">Entered Today</span>
              </div>
              {/* TOTAL */}
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-xs text-slate-500">Total</span>
                <span className="text-2xl font-bold text-white" data-testid="vehicles-entered">
                  {vehiclesEntered === null ? '—' : vehiclesEntered}
                </span>
              </div>
              {/* ROW 1 - CHARGEABLE (green): Payable */}
              <div className="flex justify-between text-xs mb-1">
                <span className="text-emerald-400 font-semibold">Payable</span>
                <span className="text-emerald-400 font-semibold" data-testid="payable-entries">
                  {vehiclesEntered === null ? '—' : (payableEntries ?? 0)}
                </span>
              </div>
              {/* ROW 2 - WHITELISTED (yellow) */}
              <div className="flex justify-between text-xs mb-1">
                <span className="text-yellow-400 font-semibold">Whitelisted</span>
                <span className="text-yellow-400 font-semibold" data-testid="whitelist-entries">
                  {vehiclesEntered === null ? '—' : (whitelistEntries ?? 0)}
                </span>
              </div>
              {/* ROW 3 - FREE (muted) */}
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">Free</span>
                <span className="text-slate-600">—</span>
              </div>
            </div>

            {/* EXITED TODAY */}
            <div className="flex flex-col md:border-r md:border-slate-800 md:px-4" data-testid="exited-today-section">
              <div className="flex items-center gap-2 mb-3">
                <LogOut className="w-5 h-5 text-emerald-400" />
                <span className="text-sm font-bold text-slate-200 uppercase tracking-wide">Exited Today</span>
              </div>
              {/* TOTAL */}
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-xs text-slate-500">Total</span>
                <span className="text-2xl font-bold text-white" data-testid="vehicles-exited">
                  {vehiclesExited === null ? '—' : vehiclesExited}
                </span>
              </div>
              {/* ROW 1 - CHARGEABLE (green): Paid */}
              <div className="flex justify-between text-xs mb-1">
                <span className="text-emerald-400 font-semibold">Paid</span>
                <span className="text-emerald-400 font-semibold" data-testid="paid-exits">
                  {vehiclesExited === null ? '—' : (paidExits ?? 0)}
                </span>
              </div>
              {/* ROW 2 - WHITELISTED (yellow) */}
              <div className="flex justify-between text-xs mb-1">
                <span className="text-yellow-400 font-semibold">Whitelisted</span>
                <span className="text-yellow-400 font-semibold" data-testid="whitelist-exits">
                  {vehiclesExited === null ? '—' : (whitelistExits ?? 0)}
                </span>
              </div>
              {/* ROW 3 - FREE (red) */}
              <div className="flex justify-between text-xs">
                <span className="text-red-400 font-semibold">Free</span>
                <span className="text-red-400 font-semibold" data-testid="free-exits">
                  {vehiclesExited === null ? '—' : (freeExits ?? 0)}
                </span>
              </div>
            </div>

            {/* LIVE NOW - Clickable */}
            <div 
              className="flex flex-col md:pl-4 cursor-pointer group"
              onClick={() => navigate('/admin/live-sessions')}
              data-testid="live-now-section"
            >
              <div className="flex items-center gap-2 mb-3">
                <Car className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-bold text-slate-200 uppercase tracking-wide">Live Now</span>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors ml-auto" />
              </div>
              {/* TOTAL */}
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-xs text-slate-500">Total</span>
                <span className="text-2xl font-bold text-white group-hover:text-cyan-400 transition-colors" data-testid="live-sessions-total">
                  {liveSessionsTotal === null ? '—' : liveSessionsTotal}
                </span>
              </div>
              {/* ROW 1 - CHARGEABLE (green): Payable */}
              <div className="flex justify-between text-xs mb-1">
                <span className="text-emerald-400 font-semibold">Payable</span>
                <span className="text-emerald-400 font-semibold" data-testid="live-payable-sessions">
                  {liveSessionsTotal === null ? '—' : (livePayableSessions ?? 0)}
                </span>
              </div>
              {/* ROW 2 - WHITELISTED (yellow) */}
              <div className="flex justify-between text-xs mb-1">
                <span className="text-yellow-400 font-semibold">Whitelisted</span>
                <span className="text-yellow-400 font-semibold" data-testid="live-whitelisted-sessions">
                  {liveSessionsTotal === null ? '—' : (liveWhitelistedSessions ?? 0)}
                </span>
              </div>
              {/* ROW 3 - FREE (muted) */}
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">Free</span>
                <span className="text-slate-600">—</span>
              </div>
            </div>
          </div>

          {/* Reconciliation Status - Below all columns */}
          {entryReconciliationOk !== null && (
            <div className="mt-4 pt-3 border-t border-slate-800">
              {entryReconciliationOk ? (
                <div className="flex items-center gap-1.5 text-sm text-emerald-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Reconciled</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-sm text-amber-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-semibold">Reconciliation issue</span>
                </div>
              )}
            </div>
          )}

          {/* Explanation Box - Always visible */}
          <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="font-medium text-slate-300">Understanding these figures:</span> Entered Today counts vehicles entering since midnight. Exited Today counts all vehicles leaving today, including vehicles that may have entered on an earlier day. Live Now shows today's entrants whose parking sessions remain open. For this reason, Entered Today minus Exited Today will not always equal Live Now.
            </p>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              ParkWise separately reconciles today's entry cohort against sessions still live and sessions completed from today's entrants.
            </p>
          </div>
        </div>

        {/* COMPACT KPI ROW - Revenue & Operations */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {/* Revenue Today */}
          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-3 cursor-pointer hover:border-emerald-500/50 transition-colors"
            onClick={() => navigate('/admin/commercial/finance?preset=today')}
            data-testid="kpi-revenue-today"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Revenue Today</span>
              <Banknote className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-xl font-bold text-white">
              {revenueToday === null ? (
                <span className="text-amber-500 text-sm">—</span>
              ) : formatCurrency(revenueToday) || <span className="text-slate-500">£0.00</span>}
            </div>
          </div>

          {/* Revenue This Week */}
          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-3 cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => navigate('/admin/commercial/finance?preset=this_week')}
            data-testid="kpi-revenue-week"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Revenue This Week</span>
              <BarChart3 className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-xl font-bold text-white">
              {revenueWeek === null ? (
                <span className="text-amber-500 text-sm">—</span>
              ) : formatCurrency(revenueWeek) || <span className="text-slate-500">£0.00</span>}
            </div>
          </div>

          {/* Average Spend */}
          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-3 cursor-pointer hover:border-purple-500/50 transition-colors"
            onClick={() => navigate('/admin/reports/daily-movement')}
            data-testid="kpi-avg-spend"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Avg Spend Today</span>
              <Banknote className="w-4 h-4 text-purple-400" />
            </div>
            <div className="text-xl font-bold text-white">
              {avgSpendToday === null ? (
                <span className="text-amber-500 text-sm">—</span>
              ) : formatCurrency(avgSpendToday) || <span className="text-slate-500">£0.00</span>}
            </div>
          </div>

          {/* OCR Exceptions */}
          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-3 cursor-pointer hover:border-amber-500/50 transition-colors"
            onClick={() => navigate('/admin/ocr-exceptions')}
            data-testid="kpi-ocr-exceptions"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">OCR Exceptions</span>
              <Eye className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-xl font-bold text-white">
              {ocrExceptions === null ? (
                <span className="text-amber-500 text-sm">—</span>
              ) : ocrExceptions === 0 ? (
                <span className="text-emerald-400">0</span>
              ) : (
                <span className="text-amber-400">{ocrExceptions}</span>
              )}
            </div>
          </div>
        </div>

        {/* RECENT SESSIONS - Live Activity (kept as approved) */}
        <div className="grid grid-cols-1 gap-6">
          {/* Recent Sessions */}
          <Card className="bg-slate-900 border-slate-800" data-testid="recent-sessions-panel">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white">Recent Sessions</CardTitle>
              <button 
                onClick={() => navigate('/admin/sessions')} 
                className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer"
                data-testid="view-all-sessions-link"
              >
                View all
              </button>
            </CardHeader>
            <CardContent>
              {sessions.total.length === 0 ? (
                <div className="text-center py-8">
                  <ParkingCircle className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500">No session data available</p>
                  <p className="text-xs text-slate-600 mt-1">Sessions will appear when vehicles enter</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.total.slice(0, 5).map((session, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          session.status === 'active' ? 'bg-green-400' : 'bg-slate-500'
                        }`} />
                        <div>
                          <p className="text-white font-mono text-sm">{session.vehicle_registration}</p>
                          <p className="text-xs text-slate-500">
                            {session.status === 'active' ? 'On site' : 'Exited'}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        session.status === 'active' 
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-slate-700 text-slate-400'
                      }`}>
                        {session.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
